
interface ChartDisplayProps {
  data: any[];
  type: 'bar' | 'line' | 'pie';
}

export default function ChartDisplay({ data, type }: ChartDisplayProps) {
  const maxValue = Math.max(...data.map(d => d.value));

  if (type === 'bar') {
    return (
      <div className="space-y-3">
        <div className="flex items-end justify-between gap-2 h-56">
          {data.map((item, index) => (
            <div key={index} className="flex-1 flex flex-col items-center gap-1.5">
              <div className="relative w-full flex-1 flex items-end">
                <div
                  className="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-lg transition-all hover:from-blue-600 hover:to-blue-500 cursor-pointer relative group"
                  style={{ height: `${(item.value / maxValue) * 100}%` }}
                >
                  <div className="absolute -top-7 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="bg-gray-900 text-white text-xs px-1.5 py-0.5 rounded whitespace-nowrap">
                      {item.value}
                    </div>
                  </div>
                </div>
              </div>
              <span className="text-xs text-gray-600 text-center">{item.name}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === 'line') {
    const points = data.map((item, index) => ({
      x: (index / (data.length - 1)) * 100,
      y: 100 - (item.value / maxValue) * 80,
    }));

    const pathD = points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
      .join(' ');

    return (
      <div className="space-y-3">
        <div className="relative h-56 bg-gray-50 rounded-lg p-3">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path
              d={`${pathD} L 100 100 L 0 100 Z`}
              fill="url(#lineGradient)"
            />
            <path
              d={pathD}
              fill="none"
              stroke="#3B82F6"
              strokeWidth="0.5"
            />
            {points.map((point, index) => (
              <g key={index}>
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="1"
                  fill="#3B82F6"
                  className="cursor-pointer hover:r-2 transition-all"
                />
              </g>
            ))}
          </svg>
          <div className="absolute bottom-0 left-0 right-0 flex justify-between px-3 pb-1.5">
            {data.map((item, index) => (
              <span key={index} className="text-xs text-gray-600">{item.name}</span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (type === 'pie') {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    let currentAngle = -90;
    const colors = [
      '#3B82F6', '#F97316', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#06B6D4'
    ];

    return (
      <div className="flex items-center justify-center gap-6">
        <div className="relative w-56 h-56">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
            {data.map((item, index) => {
              const percentage = (item.value / total) * 100;
              const angle = (percentage / 100) * 360;
              const startAngle = currentAngle;
              const endAngle = currentAngle + angle;
              
              const startRad = (startAngle * Math.PI) / 180;
              const endRad = (endAngle * Math.PI) / 180;
              
              const x1 = 50 + 40 * Math.cos(startRad);
              const y1 = 50 + 40 * Math.sin(startRad);
              const x2 = 50 + 40 * Math.cos(endRad);
              const y2 = 50 + 40 * Math.sin(endRad);
              
              const largeArc = angle > 180 ? 1 : 0;
              
              const pathData = [
                `M 50 50`,
                `L ${x1} ${y1}`,
                `A 40 40 0 ${largeArc} 1 ${x2} ${y2}`,
                `Z`
              ].join(' ');
              
              currentAngle = endAngle;
              
              return (
                <path
                  key={index}
                  d={pathData}
                  fill={colors[index % colors.length]}
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                />
              );
            })}
            <circle cx="50" cy="50" r="20" fill="white" />
          </svg>
        </div>
        <div className="space-y-1.5">
          {data.map((item, index) => {
            const percentage = ((item.value / total) * 100).toFixed(1);
            return (
              <div key={index} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: colors[index % colors.length] }}
                ></div>
                <span className="text-xs text-gray-700">{item.name}</span>
                <span className="text-xs font-medium text-gray-900">{percentage}%</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return null;
}
